import { useState, useEffect } from 'react'
import { supabase } from '@/integrations/supabase/client'

export interface Reward {
  id: string
  account_type: string
  domain_type: string
  subheader: string
  description: string
  terms_conditions: string
  level_1_discount: string
  level_2_discount: string
  level_3_discount: string
  is_active: boolean
  school_id: string | null
  created_at: string
}

export interface UserReward {
  id: string
  user_id: string
  reward_id: string
  level_achieved: number
  earned_at: string
  reward: Reward
}

export const useRewards = () => {
  const [rewards, setRewards] = useState<Reward[]>([])
  const [userRewards, setUserRewards] = useState<UserReward[]>([])
  const [loading, setLoading] = useState(true)

  const fetchRewards = async () => {
    try {
      // Get current user's school_id
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) {
        setRewards([])
        return
      }

      const { data: profile } = await supabase
        .from('profiles')
        .select('school_id')
        .eq('id', user.id)
        .single()

      const userSchoolId = profile?.school_id

      let allRewards: Reward[] = []

      // Fetch global rewards (school_id IS NULL)
      const { data: globalRewards, error: globalError } = await supabase
        .from('rewards')
        .select('*')
        .eq('is_active', true)
        .is('school_id', null)
        .order('created_at', { ascending: false })

      if (globalError) throw globalError
      allRewards = [...(globalRewards || [])]

      // Fetch school-specific rewards if user has a school
      if (userSchoolId) {
        const { data: schoolRewards, error: schoolError } = await supabase
          .from('rewards')
          .select('*')
          .eq('is_active', true)
          .eq('school_id', userSchoolId)
          .order('created_at', { ascending: false })

        if (schoolError) throw schoolError
        allRewards = [...allRewards, ...(schoolRewards || [])]
      }

      setRewards(allRewards)
    } catch (error) {
      console.error('Error fetching rewards:', error)
    }
  }

  const fetchUserRewards = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.user) {
        return
      }

      const { data, error } = await supabase
        .from('user_rewards')
        .select(`
          *,
          reward:rewards(*)
        `)
        .eq('user_id', session.user.id)
        .order('earned_at', { ascending: false })

      if (error) throw error
      setUserRewards(data || [])
    } catch (error) {
      console.error('Error fetching user rewards:', error)
    }
  }

  useEffect(() => {
    const initializeData = async () => {
      setLoading(true)
      try {
        // Fetch rewards first
        await fetchRewards()
        
        // Then fetch user rewards
        await fetchUserRewards()
      } catch (error) {
        console.error('Error initializing rewards data:', error)
      } finally {
        // Always set loading to false, regardless of results
        setLoading(false)
      }
    }

    // Add a timeout fallback to ensure loading is never stuck
    const timeoutId = setTimeout(() => {
      setLoading(false)
    }, 5000) // 5 second timeout

    initializeData()

    return () => clearTimeout(timeoutId)
  }, [])

  const getRewardsByDomain = (domain: string) => {
    return rewards.filter(reward => reward.domain_type === domain)
  }

  const getUserRewardsByDomain = (domain: string) => {
    return userRewards.filter(userReward => userReward.reward.domain_type === domain)
  }

  const getUserRewardLevel = (rewardId: string) => {
    const userReward = userRewards.find(ur => ur.reward_id === rewardId)
    return userReward ? userReward.level_achieved : 0
  }

  const hasUserEarnedReward = (rewardId: string) => {
    return userRewards.some(ur => ur.reward_id === rewardId)
  }

  return {
    rewards,
    userRewards,
    loading,
    getRewardsByDomain,
    getUserRewardsByDomain,
    getUserRewardLevel,
    hasUserEarnedReward,
    refreshRewards: fetchRewards,
    refreshUserRewards: fetchUserRewards
  }
}
